import { toJS, observable, computed, action } from "mobx";
import {
  opinionQuestions,
  demographQuestions,
  individualQuestions,
} from "./question";
import {
  ASSESS_TYPE_MEMBER,
  ASSESS_TYPE_LEADER,
  ASSESS_TYPE_TEAM,
  phases,
} from "./../constants";
import get from "lodash/get";
import { firebase } from "./../constants";
import { authStore, resultStore } from "./../stores";
import queryString from "query-string";

const COVID_RELEASE_DATE = new Date(2020, 7, 15).getTime();
const COVID_END_DATE = new Date(2021, 5, 13).getTime();
const ATTENDANCE_REENABLE_DATE = new Date(2021, 11, 3).getTime();

const enabledDemographQuestions = demographQuestions.filter((x) => {
  if (Date.now() > ATTENDANCE_REENABLE_DATE) {
    // remove [conversion] questions
    return x.id !== 104.1 && x.id !== 106;
  }
  if (Date.now() > COVID_END_DATE) {
    // remove [attendance multi, conversion] questions
    return x.id !== 104 && x.id !== 106;
  }
  // these lines below are just for reference
  if (Date.now() > COVID_RELEASE_DATE) {
    // remove [attendance multi, attendance single, conversion] questions
    return x.id !== 104 && x.id !== 104.1 && x.id !== 106;
  } else {
    // remove [ church staff, yearly conversion, new contacts] questions
    return x.id !== 104.5 && x.id !== 106.5 && x.id !== 107;
  }
});

const randomOpinionQuestions = opinionQuestions
  .slice()
  .sort(() => 0.5 - Math.random());

const setQuestionDefaults = (question) => {
  return { ...question, value: null };
};

class AssessStore {
  @observable questions = [];
  @observable index = 0; // which question
  @observable type = null; // member, team, leader
  @observable started = false; // show intro screen until true
  @observable submitted = false; // final answers for assessment
  @observable forwarding = false; // just answered and animating to next

  @observable inviteId = null;
  @observable inviteChurch = null;
  @observable inviteEmail = null;
  @observable retake = false;

  @computed
  get current() {
    return this.questions[this.index];
  }
  @computed
  get prev() {
    return this.questions[this.index - 1];
  }
  @computed
  get next() {
    return this.questions[this.index + 1];
  }
  @computed
  get answered() {
    if (this.current && typeof this.current.validator === "function") {
      return this.current.validator(this.current.value);
    }
    return !!this.current.value;
  }
  @computed
  get progress() {
    return this.index / (this.questions.length - 1);
  }
  @computed
  get completed() {
    return (
      this.questions.length &&
      this.questions.every((question) => question.value !== null)
    );
  }

  @action.bound
  getChurch() {
    if (authStore.auth && authStore.auth.uid) {
      firebase
        .database()
        .ref("church/" + authStore.auth.uid)
        .once(
          "value",
          action((snapshot) => {
            var church = snapshot.val();
            if (church) {
              this.church = church;
              this.response = church.responses.initial;
              //window.createResultsPDF(5, this.averageResponse, opinionQuestions)
            }
          })
        );
    }
  }
  @action.bound
  getChurchId() {
    var parsed = queryString.parse(location.search);
    this.churchId = parsed.church || null;
  }
  @action.bound
  start() {
    this.started = true;
  }
  @action.bound
  back() {
    if (this.prev) {
      this.index -= 1;
    }
  }
  @action.bound
  forward() {
    if (this.next && this.answered) {
      this.index += 1;
    }
  }
  @action.bound
  answer(value, forward, immediateForward) {
    if (this.forwarding) {
      return;
    }
    this.questions[this.index].value = value;

    if (immediateForward) {
      this.forward();
    } else if (forward) {
      if (process.env.NODE_ENV === "development") {
        this.forward();
      } else {
        this.forwarding = true;
        setTimeout(
          action(() => {
            this.forwarding = false;
            this.forward();
          }),
          500
        );
      }
    }
  }
  @action.bound
  loadInvite(searchParam) {
    const { church } = queryString.parse(searchParam);
    if (church) {
      this.inviteId = church;
      firebase
        .database()
        .ref(`church/${church}/churchData/name`)
        .once(
          "value",
          action((snap) => {
            this.inviteChurch = snap.val();
          })
        );
      firebase
        .database()
        .ref(`church/${church}/responses/initial/email`)
        .once(
          "value",
          action((snap) => {
            this.inviteEmail = snap.val();
          })
        );
    }
  }

  @action.bound
  beginRetake() {
    this.retake = true;
  }

  @action.bound
  submitTeam(auth, firstName, lastName) {
    resultStore.beginSubmit();
    if (window.fbq) {
      window.fbq("track", "SubmitApplication");
    }

    var response = {
      email: auth.email,
      firstName: firstName || null,
      lastName: lastName || null,
      surveyData: {
        questions: toJS(this.questions.slice(enabledDemographQuestions.length))
          .sort((a, b) => a.id - b.id)
          .map((x) => {
            return {
              id: x.id || null,
              stage: typeof x.stage === "number" ? x.stage : null,
              value: x.value || 0,
            };
          }),
        timeSubmitted: Date.now(),
      },
    };
    // prettier-ignore
    var church = {
      churchData: {
        timeCreated: Date.now(),
        name: get(this.questions.find((x) => x.id == 101), "value") || null,
        region: get(this.questions.find((x) => x.id == 102), "value") || null,
        denomination: get(this.questions.find((x) => x.id == 103), "value") || null,
        attendanceOne: get(this.questions.find((x) => x.id == 104), "value.one") || null,
        attendanceTwo: get(this.questions.find((x) => x.id == 104), "value.two") || null,
        attendanceThree: get(this.questions.find((x) => x.id == 104), "value.three") || null,
        attendanceAvg: get(this.questions.find(x => x.id == 104.1), "value") || null,
        staffSize: get(this.questions.find((x) => x.id == 104.5), "value") || null,
        givingOne: get(this.questions.find((x) => x.id == 105), "value.one") || null,
        givingTwo: get(this.questions.find((x) => x.id == 105), "value.two") || null,
        givingThree: get(this.questions.find((x) => x.id == 105), "value.three") || null,
        conversions: get(this.questions.find((x) => x.id == 106), "value") || get(this.questions.find((x) => x.id == 106.5), "value.one") || null,
        conversionsOne: get(this.questions.find((x) => x.id == 106.5), "value.one") || null,
        conversionsTwo: get(this.questions.find((x) => x.id == 106.5), "value.two") || null,
        contactsOne: get(this.questions.find((x) => x.id == 107), "value.one") || null,
        contactsTwo: get(this.questions.find((x) => x.id == 107), "value.two") || null,
      },
      responses: {
        initial: response,
      },
    };

    if (auth && auth.uid && auth.email) {
      this.submitted = true;

      firebase
        .database()
        .ref("church/" + auth.uid)
        .update(church)
        .then(resultStore.loadChurchResponse);
    } else if (this.retake) {
      this.submitted = true;
      var uid = authStore.auth.uid;
      response.email = authStore.auth.email;

      firebase
        .database()
        .ref("church/" + uid)
        .child("churchData")
        .set(church.churchData)
        .then(() =>
          firebase
            .database()
            .ref("church/" + uid)
            .child("responses")
            //.child("initial")
            .set(church.responses)
        )
        .then(resultStore.loadChurchResponse);
    }
  }

  @action.bound
  submit(email, firstName, lastName) {
    this.submitted = true;
    var response = {
      email: email,
      firstName: firstName || null,
      lastName: lastName || null,
      surveyData: {
        questions: toJS(this.questions)
          .sort((a, b) => a.id - b.id)
          .map((x) => {
            return {
              id: x.id || null,
              stage: typeof x.stage === "number" ? x.stage : null,
              value: x.value || null,
            };
          }),
        timeSubmitted: Date.now(),
      },
    };
    if (this.inviteId) {
      firebase
        .database()
        .ref("church/" + this.inviteId + "/responses")
        .push(response)
        .then(() => {
          resultStore.setLocalResponse(response);
        });
    } else {
      resultStore.setLocalResponse(response);

      var dbResponse = {
        phaseId: resultStore.phase.stage,
        ...response,
      };
      console.log({ dbResponse });
      firebase
        .database()
        .ref("response")
        .push(dbResponse)
        .then((snap) => {
          console.log("Key: " + snap.key);
        });
    }
  }

  @action.bound
  loadSample() {
    var sampleAnswers = [
      "Community Bible Church",
      "01",
      "01",
      { one: 500, two: 250, three: 100 },
      { one: 500, two: 250, three: 100 },
      "100",
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
      5,
    ];

    sampleAnswers.forEach((x) => this.answer(x, false, true));
  }

  @action.bound
  init(type) {
    if (this.type !== type || this.completed) {
      this.index = 0;
      this.started = false;
      if (type === ASSESS_TYPE_MEMBER) {
        this.type = ASSESS_TYPE_MEMBER;
        this.questions = [
          ...randomOpinionQuestions,
          ...individualQuestions,
        ].map(setQuestionDefaults);
      } else if (type === ASSESS_TYPE_TEAM) {
        this.type = ASSESS_TYPE_TEAM;
        this.questions = randomOpinionQuestions.map(setQuestionDefaults);
      } else {
        this.type = ASSESS_TYPE_LEADER;
        this.questions = [
          ...enabledDemographQuestions,
          ...randomOpinionQuestions,
        ].map(setQuestionDefaults);
      }
    }
  }
}

export default new AssessStore();



// WEBPACK FOOTER //
// ./src/assess/assess-store.js